<script setup lang="ts">
/*
 * A GameTile plus the small "info" button that opens the InfoSheet. GameTile
 * has no quick-view affordance of its own, so the button sits on this wrapper,
 * top-right of the tile; it shows with the tile's cursor (hover / focus) and
 * always on touch screens. Used by the Browse grid and the search overlay.
 * `wide` flattens the tile for the list layout.
 */
import { Info } from 'lucide-vue-next';
import { useUiStore } from '../../stores/ui';
import type { ProductSummary } from '../../types/product';
import GameTile from '../GameTile.vue';

const props = defineProps<{ product: ProductSummary; /** List layout: a flatter, wider tile. */ wide?: boolean }>();
const ui = useUiStore();

function openInfo() {
  // Search overlay sits above the sheet; hand over to it.
  ui.closePalette();
  ui.openQuickView(props.product.id);
}
</script>

<template>
  <div class="group/info relative">
    <GameTile :product="product" :class="wide ? '!aspect-[3/1] sm:!aspect-[5/1]' : ''" />
    <button
      type="button"
      class="btn-icon absolute right-2 top-2 z-10 !h-9 !w-9 opacity-0 transition-opacity duration-150 group-hover/info:opacity-100 group-focus-within/info:opacity-100 pointer-coarse:opacity-100"
      :aria-label="$t('catalog.quickView.open', { name: product.name })"
      @click="openInfo"
    >
      <Info class="h-4 w-4" aria-hidden="true" />
    </button>
  </div>
</template>
